import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { NotificationsLanguageModal } from '../../../../../i18n'
import { LOADING_STRING } from '../../../../NonReact/constants/Common'
import { GROUPS } from '../../../../NonReact/constants/DeviceConstants'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../../ReactRouting/Hooks/useUserInfo'
import { useFormContext, UseFormMethods, ValidateResult } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { RESTAPIError } from '../../../Common/API'
import {
    ALL_VALUE,
    getDevicesFromPartition,
    getPartitionsForTelServer,
    getTelServersAsSelectOptions,
    INVALID_VALUE
} from '../../../Common/API/ItemsGetters'
import { ComboBox } from '../../../Common/Components/ComboBox/ComboBox'
import FormRow from '../../../Common/Components/FormRow'
import Heading, { HeadingLevel } from '../../../Common/Components/Heading'
import Label from '../../../Common/Components/Label'
import { SelectOptions } from '../../../Common/Components/Selects'
import { ComboBoxItem, SelectItem } from '../../../Common/Components/Selects/Types'
import { ValidationErrors } from '../../../Common/Components/ValidationErrors/ValidationErrors'
import { SERVER_ALL_VALUE, SERVER_UNKNOWN_VALUE } from '../../../Common/Constants/ServerValues'
import { MessageContext, ReactState } from '../../../Common/Contexts'
import { MessageContextType } from '../../../Common/Contexts/MessageContext'
import { handleUnknownForAvailables, removeUnknownOption } from '../../../Common/Functions/CommonFunction'
import { FilterDefaults } from '../../../Common/Functions/FilterDefaults'
import { StateSetter } from '../../../Common/Types/Types'
import { NONE_SELECT, notificationChannels } from '../Constants'
import {
    DEVICE_SCOPE_NAME,
    ENABLED_NAME,
    NOTIFICATION_CHANNEL_NAME,
    PARTITION_SCOPE_NAME,
    TELEPHONY_SERVER_SCOPE_NAME
} from '../FieldNames'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import { ApplicationResponseNotification } from '../../../../NonReact/common/Types/Types'
import { SelectOption } from '../../../Common/Components/Selects/Select/Select'
import { RouterCheckbox } from '../../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { StatusCodes } from '../../../../NonReact/constants/StatusCodes'

type DefaultAvailables = [SelectOptions<number>, SelectOptions<number>]

/**
 * Get the available entities defaults.
 *
 * @param scope - Form permissions from the server.
 *
 * @returns - An array of defaults.
 */
const getAvailableEntitiesDefaults = (): DefaultAvailables => [
    [{ label: LOADING_STRING, value: ALL_VALUE }],
    [
        {
            label: LOADING_STRING,
            value: ALL_VALUE
        }
    ]
]

type Props = {
    NotificationSettings: ApplicationResponseNotification
}

const Notifications: FC<Props> = ({ NotificationSettings }: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(NotificationsLanguageModal.NAME)

    const { displayError }: MessageContextType = useContext(MessageContext)

    const [userInfo]: UserInfo = useUserInfo()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [telSysDefaults, partitionDefaults]: DefaultAvailables = getAvailableEntitiesDefaults()

    const [availableTelServers, setAvailableTelServers]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(telSysDefaults)

    const [availablePartitions, setAvailablePartitions]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(partitionDefaults)

    // Loading state
    const [isLoadingDevices, setIsLoadingDevices]: ReactState<boolean> = useState<boolean>(false)

    const [availableDevices, setAvailableDevices]: ReactState<SelectItem[]> = useState<SelectItem[]>([])

    const [isLoadingTelServers, setIsLoadingTelServers]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingPartitions, setIsLoadingPartitions]: ReactState<boolean> = useState<boolean>(false)

    const isLoading: boolean = isLoadingTelServers || isLoadingPartitions

    const filterDefaults: FilterDefaults = FilterDefaults.getInstance(language)

    // Contexts
    const { watch, setValue }: UseFormMethods = useFormContext()

    const enabled: boolean = watch<string, boolean>(ENABLED_NAME)
    const telServerValue: number = watch<string, number>(TELEPHONY_SERVER_SCOPE_NAME)
    const partitionValue: number[] = watch<string, number[]>(PARTITION_SCOPE_NAME)
    const devicesValue: string = watch<string, string>(DEVICE_SCOPE_NAME)

    const isSingleTelServerUser: boolean =
        (userInfo?.Scope?.TelSysID?.length ?? 0) > 0 && userInfo?.Scope.TelSysID[0] !== ALL_VALUE
    const isSinglePartitionUser: boolean =
        (userInfo?.Scope?.PartIDInTelSys?.length ?? 0) > 0 && userInfo?.Scope.PartIDInTelSys[0] !== ALL_VALUE

    const [hasTelSysFetched, setHasTelSysFetched]: ReactState<boolean> = useState<boolean>(false)

    // Create an error handler for an individual loader.
    const handleError =
        (...loadingSetters: StateSetter<boolean>[]): ((e: RESTAPIError) => void) =>
        (e: RESTAPIError): void => {
            loadingSetters.forEach((loadingSetter: StateSetter<boolean>): void => {
                if (isMounted.current) {
                    loadingSetter(false)
                }
            })
            displayError(e)
        }

    useEffect((): void => {
        if (
            !IsVarEmpty(userInfo) &&
            !IsStringEmpty(userInfo?.Scope?.CMEnvironmentID) &&
            userInfo?.Scope?.CMEnvironmentID !== SERVER_UNKNOWN_VALUE
        ) {
            setIsLoadingTelServers(true)
            getTelServersAsSelectOptions(userInfo?.Scope.CMEnvironmentID, true)
                .then((telServers: SelectOptions<number>): void => {
                    if (isMounted.current) {
                        setIsLoadingTelServers(false)

                        let isTelSysDeleted: boolean = false

                        if (
                            IsVarEmpty(NotificationSettings?.NotificationsTelSysID) &&
                            isVarNotEmpty(telServers) &&
                            telServers.length >= 1 &&
                            isSingleTelServerUser
                        ) {
                            setValue(TELEPHONY_SERVER_SCOPE_NAME, userInfo?.Scope?.TelSysID[0])
                        } else {
                            isTelSysDeleted =
                                Array.isArray(telServers) &&
                                telServers.length > 0 &&
                                !telServers.some(
                                    (telSys: SelectOption<number>): boolean => telSys.value === telServerValue
                                )

                            if (isTelSysDeleted) {
                                setValue(TELEPHONY_SERVER_SCOPE_NAME, INVALID_VALUE)
                                handleUnknownForAvailables([], setAvailablePartitions, partitionValue)
                                handleUnknownForAvailables([], setAvailableDevices, devicesValue)
                            }
                        }

                        handleUnknownForAvailables(
                            telServers,
                            setAvailableTelServers,
                            isTelSysDeleted ? INVALID_VALUE : telServerValue,
                            isTelSysDeleted
                        )

                        setHasTelSysFetched(true)
                    }
                })
                .catch((e: RESTAPIError): void => {
                    if (e.HTTPStatus !== StatusCodes.OK_NO_CONTENT && e.HTTPStatus !== StatusCodes.CREATED) {
                        displayError(e)
                    }

                    handleUnknownForAvailables([], setAvailableTelServers, telServerValue, true)
                    handleUnknownForAvailables([], setAvailablePartitions, partitionValue)
                })
                .finally((): void => {
                    if (isMounted.current) {
                        setIsLoadingTelServers(false)
                    }
                })
        } else {
            handleUnknownForAvailables([], setAvailableTelServers, telServerValue)
            handleUnknownForAvailables([], setAvailablePartitions, partitionValue)
        }
    }, [userInfo])

    useEffect((): void => {
        if (
            isVarNotEmpty(userInfo) &&
            !IsVarEmpty(telServerValue) &&
            telServerValue !== ALL_VALUE &&
            telServerValue !== INVALID_VALUE &&
            hasTelSysFetched
        ) {
            setIsLoadingPartitions(true)
            getPartitionsForTelServer(telServerValue)
                .then((partitions: SelectOptions<number>): void => {
                    if (isMounted.current) {
                        setIsLoadingPartitions(false)
                        if (
                            IsVarEmpty(NotificationSettings?.NotificationsPartitionIDInTelSys) &&
                            isVarNotEmpty(partitions) &&
                            partitions.length >= 1 &&
                            isSinglePartitionUser
                        ) {
                            setValue(PARTITION_SCOPE_NAME, [userInfo?.Scope?.PartIDInTelSys[0]])
                        }
                        handleUnknownForAvailables(partitions, setAvailablePartitions, partitionValue)
                    }
                })
                .catch((err: Error) => {
                    setIsLoadingPartitions(false)
                    displayError(err)
                })
        } else {
            handleUnknownForAvailables([], setAvailablePartitions, partitionValue)
        }

        removeUnknownOption(setAvailablePartitions, telServerValue)
    }, [telServerValue, userInfo, hasTelSysFetched])

    useEffect((): void => {
        // What to do when the partition value changes.
        if (!IsVarEmpty(partitionValue) && partitionValue.length > 0) {
            const partVal: number = partitionValue[0]

            if (
                partitionValue.length === 1 &&
                partVal !== ALL_VALUE &&
                telServerValue !== INVALID_VALUE &&
                telServerValue !== ALL_VALUE &&
                partVal !== INVALID_VALUE
            ) {
                setIsLoadingDevices(true)
                getDevicesFromPartition(telServerValue, partVal, false, GROUPS)
                    .then(([devices]: [SelectItem[], Definable<number>]): void => {
                        if (isMounted.current) {
                            setIsLoadingDevices(false)
                            handleUnknownForAvailables(devices, setAvailableDevices, devicesValue)
                        }
                    })
                    .catch(handleError(setIsLoadingDevices))
            } else {
                handleUnknownForAvailables([], setAvailableDevices, devicesValue)
            }
        }

        removeUnknownOption(setAvailablePartitions, partitionValue)

        // The stringify is needed as array values don't get evaluated properly.
    }, [JSON.stringify(partitionValue)])

    useEffect((): void => {
        removeUnknownOption(setAvailableDevices, devicesValue)
    }, [devicesValue])

    return (
        <div className='notifications-tab tab-area'>
            <Heading level={HeadingLevel.ONE}>
                {language.get(NotificationsLanguageModal.TabSettingsHeaderGeneral)}
            </Heading>
            <FormRow isInline>
                <Label customClass='label-left'>
                    {language.get(NotificationsLanguageModal.TabSettingsDoNotDisturb)}
                </Label>
                <RouterCheckbox name={ENABLED_NAME} />
            </FormRow>
            <Heading level={HeadingLevel.ONE}>
                {language.get(NotificationsLanguageModal.TabSettingsHeaderScopeFiltering)}
            </Heading>
            <FormRow>
                <ComboBox
                    variant='outlined'
                    name={TELEPHONY_SERVER_SCOPE_NAME}
                    label={language.get(NotificationsLanguageModal.TabSettingsTelServer)}
                    defaultOption={availableTelServers[0]}
                    options={availableTelServers.slice(1)}
                    loading={isLoadingTelServers}
                    noChange={isLoading}
                    onChange={(telServer: Nullable<ComboBoxItem<number>>): void => {
                        setValue(TELEPHONY_SERVER_SCOPE_NAME, (telServer as ComboBoxItem<number>).value)
                        setValue(PARTITION_SCOPE_NAME, [ALL_VALUE])
                        setValue(DEVICE_SCOPE_NAME, SERVER_ALL_VALUE)
                    }}
                    disabled={
                        !enabled ||
                        (isSingleTelServerUser &&
                            !availableTelServers.some(
                                (telServer: SelectOption<number>): boolean => telServer.value === INVALID_VALUE
                            ))
                    }
                />
            </FormRow>
            {!ArrayUtils.isEmpty(availablePartitions) ? (
                <FormRow>
                    <ValidationErrors name={PARTITION_SCOPE_NAME}>
                        <ComboBox
                            variant='outlined'
                            name={PARTITION_SCOPE_NAME}
                            label={language.get(NotificationsLanguageModal.TabSettingsPartition)}
                            multiple
                            defaultOption={
                                isSinglePartitionUser ? availablePartitions[0] : filterDefaults.getAnyPartitionOption()
                            }
                            options={isSinglePartitionUser ? availablePartitions.slice(1) : availablePartitions}
                            loading={isLoadingPartitions}
                            noChange={isLoading}
                            disabled={!enabled || isSinglePartitionUser}
                            rules={{
                                validate: (value: number[]): ValidateResult => {
                                    if (value.length > 5) {
                                        return language.get(NotificationsLanguageModal.TabSetttingsPartitionValidation)
                                    }

                                    return undefined
                                }
                            }}
                            onChange={(): void => {
                                setValue(DEVICE_SCOPE_NAME, SERVER_ALL_VALUE)
                            }}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            <FormRow>
                <ValidationErrors name={DEVICE_SCOPE_NAME}>
                    <ComboBox
                        variant='outlined'
                        name={DEVICE_SCOPE_NAME}
                        label={language.get(NotificationsLanguageModal.TabSettingsDevices)}
                        defaultOption={filterDefaults.getAnyDevice()}
                        options={availableDevices}
                        loading={isLoadingDevices}
                        noChange={isLoading}
                        disabled={!enabled}
                    />
                </ValidationErrors>
            </FormRow>
            <Heading level={HeadingLevel.ONE}>
                {language.get(NotificationsLanguageModal.TabSettingsNotificationChannels)}
            </Heading>
            <FormRow>
                <ComboBox
                    variant='outlined'
                    name={NOTIFICATION_CHANNEL_NAME}
                    label={language.get(NotificationsLanguageModal.TabSettingsChannels)}
                    multiple
                    defaultOption={{
                        label: NONE_SELECT,
                        value: ALL_VALUE
                    }}
                    noChange={isLoading}
                    options={notificationChannels}
                    disabled={!enabled}
                />
            </FormRow>
        </div>
    )
}

export default Notifications
